import { Sun, Moon, Desktop } from '@phosphor-icons/react';

import { usePlatformThemeStore, type PlatformThemeOption } from '@/lib/platformThemeStore';

/**
 * Light · Dark · System, for the Appearance section.
 *
 * This control used to live in the sidebar footer. It moved because a preference is not
 * navigation: the footer of a console sidebar is for identity, and a colour switch sitting there
 * permanently is a setting in the wrong room. It is now reached where every other preference is
 * reached — Settings → Appearance & Regional.
 *
 * A segmented control rather than a single toggle, because "System" is a real third choice, not
 * the absence of one — a toggle can only express two states and would silently drop it.
 *
 * It writes through `usePlatformThemeStore` and nothing else. There is exactly one theme store
 * for the console (see that file's header for why it is separate from the chambers one), so
 * switching here is the same act as switching anywhere: it touches no query key, so no page is
 * refetched and no table loses its filters or cursor.
 */

const OPTIONS: Array<{ value: PlatformThemeOption; label: string; Icon: typeof Sun; hint: string }> = [
  { value: 'light',  label: 'Light',  Icon: Sun,     hint: 'Light content, navy navigation' },
  { value: 'dark',   label: 'Dark',   Icon: Moon,    hint: 'Dark content, navy navigation' },
  { value: 'system', label: 'System', Icon: Desktop, hint: 'Follow this device' },
];

export function PlatformThemeChoice() {
  const theme = usePlatformThemeStore((s) => s.theme);
  const setTheme = usePlatformThemeStore((s) => s.setTheme);

  return (
    <div>
      <div
        className="flex flex-col gap-2 sm:flex-row"
        role="radiogroup"
        aria-label="Colour theme"
      >
        {OPTIONS.map(({ value, label, Icon, hint }) => {
          const selected = theme === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              title={label}
              onClick={() => setTheme(value)}
              className={[
                'flex flex-1 items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus',
                selected
                  ? 'border-plat-focus bg-plat-selected'
                  : 'border-plat-border bg-plat-bg hover:bg-plat-hover',
              ].join(' ')}
            >
              <Icon
                className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-plat-info-text' : 'text-plat-muted'}`}
                weight={selected ? 'fill' : 'regular'}
              />
              <span className="min-w-0">
                <span className={`block text-sm font-medium ${selected ? 'text-plat-fg' : 'text-plat-secondary'}`}>
                  {label}
                </span>
                <span className="mt-0.5 block text-xs text-plat-muted">{hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-plat-muted">
        Applied immediately and remembered for your account on this browser. It is stored on this
        device, so a different computer keeps its own choice.
      </p>
    </div>
  );
}
